import React from 'react';
import { isFunction, isNil, isObject } from 'lodash';
import { MaskedInputBaseProps } from './types';
import { useProps, useTheme } from '../../utils';
import {
  createBlurHandler,
  createChangeHandler,
  createClearHandler,
  createFocusHandler,
  createKeyDownHandler,
  createPasteHandler,
} from './handlers';
import {
  checkIsEmptyValue,
  getEmptyValue,
  maskValue,
  getValue,
} from './helpers';
import { DEFAULT_PLACEHOLDER_CHAR } from './constants';
import { useAdjustCursor } from '../../utils/useAdjustCursor';
import { COMPONENTS_NAMESPACES } from '../../constants';

export const MaskedInputBase = React.forwardRef((props: MaskedInputBaseProps, ref?: React.Ref<HTMLInputElement | null>) => {
  const {
    className,
    hasClearButton,
    mask,
    value,
    placeholderChar = DEFAULT_PLACEHOLDER_CHAR,
    placeholder,
    isDisabled = false,
    inputValue: inputValueProp,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseDown,
    theme: themeProp,
    ...restProps
  } = useProps(props);

  const [isFocused, setFocused] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [inputValue, setInputValue] = React.useState<string>(isNil(inputValueProp) ? '' : inputValueProp);

  const emptyValue = getEmptyValue(mask, placeholderChar);

  const isEmptyValue = checkIsEmptyValue(inputValue, emptyValue);

  const shouldRenderClearButton = hasClearButton && !isDisabled && !isEmptyValue;

  const theme = useTheme(themeProp, COMPONENTS_NAMESPACES.maskedInput);

  React.useEffect(() => {
    if (!isNil(inputValueProp)) {
      setInputValue(inputValueProp);
    }
  }, [inputValueProp]);

  const adjustCursor = useAdjustCursor();

  const handleKeyDown = createKeyDownHandler(props, {
    inputRef,
    isFocused,
  });

  const handlePaste = createPasteHandler(props);

  const handleFocus = createFocusHandler(props, {
    inputRef,
    inputValue,
    isFocused,
    setFocused,
    setInputValue,
  });

  const handleBlur = createBlurHandler(props, {
    inputValue,
    mask,
    placeholderChar,
    setFocused,
    setInputValue,
  });

  const handleChange = createChangeHandler(props, {
    adjustCursor,
    inputValue,
    setInputValue,
  });

  const handleClearValue = createClearHandler(props, {
    inputValue,
    setInputValue,
  });

  React.useEffect(() => {
    if (!isFocused) {
      const newValue = value === ''
        ? ''
        : maskValue(value, mask, placeholderChar);

      setInputValue(newValue);
    }
  }, [value]);

  React.useEffect((): void => {
    const isAutofocused = isFocused && !inputValue;
    // If value is empty and input is not autofocused, don't do anything.
    if (!value && !isAutofocused) return;

    const maskedValue = maskValue(value, mask, placeholderChar);

    // If isFocused is true and input value is not match mask, replacing input value with mask value.
    if (isFocused && inputValue !== maskedValue) {
      setInputValue(maskedValue);
    }
  }, [inputValue, isFocused, mask, placeholderChar, value]);

  return (
    <>
      <input
        disabled={isDisabled}
        className={className}
        maxLength={mask.length + 1}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onChange={handleChange}
        placeholder={placeholder}
        value={getValue({
          inputValue,
          isFocused,
          mask,
          placeholderChar,
          valueProp: value,
        })}
        {...restProps}
        ref={(component) => {
          if (isFunction(ref)) ref(component);
          else if (isObject(ref)) {
            // @ts-ignore
            ref.current = component;
          }

          inputRef.current = component;
        }}
      />
      {shouldRenderClearButton && (
      <i
        className={theme.closeIcon}
        onMouseDown={handleClearValue}
      />
      )}
    </>
  );
}) as React.FC<MaskedInputBaseProps>;

MaskedInputBase.displayName = 'MaskedInputBase';
